import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { useWishlistStore } from '../store/wishlistStore';
import {
  FiShoppingCart, FiUser, FiHeart, FiSearch,
  FiLogOut, FiMenu, FiX, FiChevronDown, FiChevronRight, FiPackage,
} from 'react-icons/fi';
import './Header.css';

const DEFAULT_NAV = [
  { label: 'Samyang',     to: '/products', dropdown: true },
  { label: 'Nongshim',    to: '/products', dropdown: true },
  { label: 'Sauces',      to: '/products'                 },
  { label: 'Chopsticks',  to: '/products'                 },
  { label: 'Tteokbokki',  to: '/products'                 },
  { label: 'Sushi Sheet', to: '/products'                 },
  { label: 'Rice Paper',  to: '/products'                 },
  { label: 'Most Selling', to: '/most-selling'              },
  { label: 'Track Order',  to: '/orders'                   },
];

function sendEdit(section) {
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'SECTION_CLICKED', section }, '*');
  }
}

function EditChip({ label }) {
  return <span className="edit-chip">✏ {label}</span>;
}

function Header() {
  const { token, logout }                  = useAuthStore();
  const { getCartItemsCount, openDrawer }  = useCartStore();
  const { settings, editMode }             = useStoreSettings();
  const wishlistCount                      = useWishlistStore(s => s.getCount());
  const navigate                           = useNavigate();
  const location                           = useLocation();
  const [searchParams]                     = useSearchParams();
  const [mobileOpen,     setMobileOpen]    = useState(false);
  const [searchOpen,     setSearchOpen]    = useState(false);
  const [hoveredNav,     setHoveredNav]    = useState(null);
  const [hoveredSub,     setHoveredSub]    = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [allCategories,  setAllCategories] = useState([]);
  const [catMenuOpen,    setCatMenuOpen]   = useState(false);
  const [hoveredCat,     setHoveredCat]    = useState(null);
  const closeTimer    = useRef(null);
  const subCloseTimer = useRef(null);
  const catMenuRef    = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (catMenuRef.current && !catMenuRef.current.contains(e.target)) setCatMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch all categories once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/categories`)
      .then(r => r.json())
      .then(data => Array.isArray(data) && setAllCategories(data))
      .catch(() => {});
  }, []);

  // Returns subcategories whose parent name matches the nav label
  const getSubcategories = (label) => {
    const parent = allCategories.find(
      c => c.name.toLowerCase() === label.toLowerCase() && !c.parent_id
    );
    if (!parent) return [];
    return allCategories.filter(c => c.parent_id === parent.id);
  };

  const getChildren = (parentId) => allCategories.filter(c => c.parent_id === parentId);

  const isDropdownItemActive = (catId) => {
    const activeCatId = parseInt(searchParams.get('category_id') || searchParams.get('cat')) || null;
    if (!activeCatId) return false;
    if (activeCatId === catId) return true;
    return allCategories.some(c => c.parent_id === catId && c.id === activeCatId);
  };

  // Parse category id out of any link.to (/products, /products?cat=3, /products?category_id=3)
  const linkCatId = (link) => {
    if (!link.to || !link.to.includes('?')) return null;
    const p = new URLSearchParams(link.to.split('?')[1]);
    const v = p.get('category_id') || p.get('cat');
    return v ? parseInt(v) : null;
  };

  const getCategoryLink = (link) => {
    if (!link.to) return '/products';
    const basePath = link.to.split('?')[0];
    if (basePath !== '/products') return link.to;
    // Normalize both ?cat= and ?category_id= to ?category_id=
    const embedded = linkCatId(link);
    if (embedded) return `/products?category_id=${embedded}`;
    if (link.category_id) return `/products?category_id=${link.category_id}`;
    const match =
      allCategories.find(c => c.name.toLowerCase() === link.label.toLowerCase() && !c.parent_id) ||
      allCategories.find(c => c.name.toLowerCase() === link.label.toLowerCase());
    return match ? `/products?category_id=${match.id}` : link.to;
  };

  const isNavLinkActive = (link) => {
    if (!link.to) return false;
    const basePath = link.to.split('?')[0];
    if (basePath !== '/products') return location.pathname === basePath;

    // Active category from current URL (support both param names)
    const raw = searchParams.get('category_id') || searchParams.get('cat');
    const activeCatId = raw ? parseInt(raw) : null;
    if (!activeCatId) return false;

    // Direct match via embedded id in link
    const directId = linkCatId(link);
    if (directId === activeCatId) return true;

    // Match via category name = link label, then check self + descendants
    const byLabel  = allCategories.find(c => c.name.toLowerCase() === link.label.toLowerCase() && !c.parent_id);
    const byDirect = directId ? allCategories.find(c => c.id === directId) : null;
    const resolved = byLabel || byDirect;
    if (!resolved) return false;
    if (activeCatId === resolved.id) return true;
    // Check one level of children (expand to recursive if needed)
    return allCategories.some(c => c.parent_id === resolved.id && c.id === activeCatId);
  };

  const handleNavEnter = (label) => {
    clearTimeout(closeTimer.current);
    setHoveredNav(label);
  };

  const handleNavLeave = () => {
    closeTimer.current = setTimeout(() => setHoveredNav(null), 300);
  };

  const handleLogout = () => { logout(); navigate('/'); setMobileOpen(false); };

  const { ticker, logo, social = {}, topBanner } = settings;
  const navLinks = settings.navLinks ?? DEFAULT_NAV;
  const msgs = ticker?.messages?.length ? ticker.messages : ['Free Shipping on orders over Rs 2000'];

  const [tickerIdx,  setTickerIdx]  = useState(0);
  const [tickerFade, setTickerFade] = useState(true);

  useEffect(() => {
    if (msgs.length < 2) return;
    const id = setInterval(() => {
      setTickerFade(false);
      setTimeout(() => {
        setTickerIdx(i => (i + 1) % msgs.length);
        setTickerFade(true);
      }, 350);
    }, 4000);
    return () => clearInterval(id);
  }, [msgs.length]);

  return (
    <>
      {/* ── Announcement Banner — scrolls away ──────── */}
      {ticker?.image_url && ticker?.enabled !== false && (
        <div
          className={editMode ? 'editable-section' : undefined}
          style={{ position: 'relative', lineHeight: 0 }}
          onClick={editMode ? () => sendEdit('ticker') : undefined}
        >
          {editMode && <EditChip label="Edit Banner" />}
          <img src={ticker.image_url} alt="Announcement Banner"
            style={{ width: '100%', height: 68, objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      {/* ── Header wrapper — sticky ─────────────────── */}
      <header className="header-wrapper">

        {/* Top row */}
        <div className="header-top">
          <div className="header-top-inner">

            {/* Left: Hamburger (mobile) + Logo */}
            <div className="header-left">
              <button className="hdr-mobile-toggle" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
                {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
              </button>
              <div
                className={editMode ? 'editable-section' : undefined}
                style={{ position: 'relative', display: 'inline-flex' }}
                onClick={editMode ? (e) => { e.preventDefault(); sendEdit('logo'); } : undefined}
              >
                {editMode && <EditChip label="Edit Logo" />}
                <Link to="/" className="logo-center" onClick={editMode ? e => e.preventDefault() : undefined}>
                  {logo?.image_url
                    ? <img src={logo.image_url} alt={logo?.text || 'Seoul Station'} style={{ height: 48, maxWidth: 180, objectFit: 'contain', display: 'block' }} />
                    : <span className="logo-name" style={{ color: logo?.color || '#111111' }}>
                        {logo?.text || 'Seoul'}{' '}
                        <span style={{ color: logo?.accent_color || '#c0392b' }}>{logo?.dot || 'Station'}</span>
                      </span>
                  }
                </Link>
              </div>
            </div>

            {/* Categories dropdown */}
            <div className="hdr-cat-wrap" ref={catMenuRef}>
              <button className={`hdr-cat-btn${catMenuOpen ? ' open' : ''}`} onClick={() => !editMode && setCatMenuOpen(o => !o)}>
                <FiMenu size={16} />
                <span>CATEGORIES</span>
                <FiChevronDown size={14} className="hdr-cat-chevron" />
              </button>
              {catMenuOpen && (
                <div className="hdr-cat-dropdown">
                  {allCategories.filter(c => !c.parent_id).map(cat => {
                    const children = allCategories.filter(c => c.parent_id === cat.id);
                    return (
                      <div key={cat.id} className="hdr-cat-item-wrap" onMouseEnter={() => setHoveredCat(cat.id)} onMouseLeave={() => setHoveredCat(null)}>
                        <Link to={`/products?category_id=${cat.id}`} className="hdr-cat-item" onClick={() => setCatMenuOpen(false)}>
                          {cat.name}
                          {children.length > 0 && <FiChevronRight size={13} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                        </Link>
                        {children.length > 0 && hoveredCat === cat.id && (
                          <div className="hdr-cat-sub">
                            {children.map(child => (
                              <Link key={child.id} to={`/products?category_id=${child.id}`} className="hdr-cat-item" onClick={() => setCatMenuOpen(false)}>{child.name}</Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* All Products button */}
            <Link to="/products" className="hdr-all-btn">ALL PRODUCTS</Link>

            {/* Center: Search bar (desktop) */}
            <form
              className="header-search-form"
              onSubmit={e => {
                e.preventDefault();
                const q = e.target.elements.q.value.trim();
                if (q && !editMode) navigate(`/products?q=${encodeURIComponent(q)}`);
              }}
            >
              <div className="header-search-inner">
                <input name="q" type="text" placeholder="Search products…" />
                <button type="submit" className="hdr-search-btn"><FiSearch size={17} /></button>
              </div>
            </form>

            {/* Right: Account, Wishlist, Cart with labels */}
            <div className="header-actions">
              {/* Mobile search icon */}
              <button className="hdr-action-btn hdr-search-mobile-btn" onClick={() => setSearchOpen(o => !o)}>
                {searchOpen ? <FiX size={22} /> : <FiSearch size={22} />}
              </button>
              <Link to="/orders" className="hdr-action-btn hdr-orders-btn">
                <FiPackage size={22} />
                <div className="hdr-action-text">
                  <span className="hdr-action-sub">My</span>
                  <span className="hdr-action-label">Track Order</span>
                </div>
              </Link>
              <Link to="/wishlist" className="hdr-action-btn hdr-wishlist-btn">
                <div className="hdr-action-icon-wrap">
                  <FiHeart size={22} />
                  {wishlistCount > 0 && <span className="hdr-badge">{wishlistCount}</span>}
                </div>
                <div className="hdr-action-text">
                  <span className="hdr-action-sub">{wishlistCount} item{wishlistCount !== 1 ? 's' : ''}</span>
                  <span className="hdr-action-label">Wishlist</span>
                </div>
              </Link>
              <button className="hdr-action-btn" onClick={() => !editMode && openDrawer()}>
                <div className="hdr-action-icon-wrap">
                  <FiShoppingCart size={22} />
                  {getCartItemsCount() > 0 && <span className="hdr-badge">{getCartItemsCount()}</span>}
                </div>
                <div className="hdr-action-text">
                  <span className="hdr-action-sub">{getCartItemsCount()} item{getCartItemsCount() !== 1 ? 's' : ''}</span>
                  <span className="hdr-action-label">Cart</span>
                </div>
              </button>
              {token ? (
                <>
                  <Link to="/profile" className="hdr-action-btn hdr-user-btn">
                    <FiUser size={22} />
                    <span className="hdr-action-label">Account</span>
                  </Link>
                  <button className="hdr-action-btn hdr-user-btn" onClick={handleLogout}>
                    <FiLogOut size={22} />
                    <span className="hdr-action-label">Logout</span>
                  </button>
                </>
              ) : (
                <Link to="/login" className="hdr-action-btn hdr-user-btn">
                  <FiUser size={22} />
                  <div className="hdr-action-text">
                    <span className="hdr-action-sub">Hello,</span>
                    <span className="hdr-action-label">Sign In</span>
                  </div>
                </Link>
              )}
            </div>

          </div>
        </div>

        {/* Mobile expandable search bar */}
        {searchOpen && (
          <div className="header-search-mobile">
            <form onSubmit={e => {
              e.preventDefault();
              const q = e.target.elements.q.value.trim();
              if (q && !editMode) { navigate(`/products?q=${encodeURIComponent(q)}`); setSearchOpen(false); }
            }}>
              <div className="header-search-inner">
                <input autoFocus name="q" type="text" placeholder="Search products…" />
                <button type="submit" className="hdr-search-btn"><FiSearch size={17} /></button>
              </div>
            </form>
          </div>
        )}

        {/* Nav */}
        <nav
          className={`header-nav ${mobileOpen ? 'open' : ''} ${editMode ? 'editable-section' : ''}`}
          style={{ position: 'relative' }}
          onClick={editMode ? () => sendEdit('nav') : undefined}
        >
          {editMode && <EditChip label="Edit Navigation" />}
          <div className="header-nav-inner">
            <div className="nav-main-links-hidden">
            {navLinks.map(link => {
              const subs = getSubcategories(link.label);
              const hasDropdown = subs.length > 0;
              const isOpen = hoveredNav === link.label;

              const isMobileOpen = mobileExpanded === link.label;

              return (
                <div
                  key={link.label}
                  className="nav-item"
                  onMouseEnter={() => hasDropdown && handleNavEnter(link.label)}
                  onMouseLeave={handleNavLeave}
                >
                  <div className="nav-link-row">
                    <NavLink
                      to={getCategoryLink(link)}
                      end={link.end}
                      className={() => `nav-link ${isNavLinkActive(link) ? 'active' : ''}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </NavLink>
                    {hasDropdown && (
                      <button
                        className={`nav-chevron-btn${isMobileOpen ? ' open' : ''}`}
                        onClick={e => { e.preventDefault(); setMobileExpanded(isMobileOpen ? null : link.label); }}
                        aria-label="Toggle subcategories"
                      >
                        <FiChevronDown size={15} />
                      </button>
                    )}
                  </div>

                  {hasDropdown && (isOpen || isMobileOpen) && (
                    <div className="nav-dropdown">
                      {subs.map((sub, idx) => {
                        const children = getChildren(sub.id);
                        const hasChildren = children.length > 0;
                        const isSubOpen = hoveredSub === sub.id;
                        const isActive = isDropdownItemActive(sub.id);
                        return (
                          <div
                            key={sub.id}
                            className="nav-dropdown-wrap"
                            onMouseEnter={() => { clearTimeout(subCloseTimer.current); setHoveredSub(sub.id); }}
                            onMouseLeave={() => { subCloseTimer.current = setTimeout(() => setHoveredSub(null), 200); }}
                          >
                            <Link
                              to={`/products?category_id=${sub.id}`}
                              className={`nav-dropdown-item${isActive ? ' active' : ''}`}
                              style={{ animationDelay: `${idx * 0.04}s` }}
                              onClick={() => { setHoveredNav(null); setHoveredSub(null); setMobileOpen(false); }}
                            >
                              {sub.name}
                              {hasChildren && <FiChevronRight size={13} className="sub-chevron" />}
                            </Link>
                            {hasChildren && isSubOpen && (
                              <div className="nav-sub-dropdown">
                                {children.map((child, ci) => (
                                  <Link
                                    key={child.id}
                                    to={`/products?category_id=${child.id}`}
                                    className={`nav-dropdown-item${parseInt(searchParams.get('category_id')) === child.id ? ' active' : ''}`}
                                    style={{ animationDelay: `${ci * 0.04}s` }}
                                    onClick={() => { setHoveredNav(null); setHoveredSub(null); setMobileOpen(false); }}
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            </div>

            {/* Mobile categories drawer */}
            <div className="mob-cat-list">
              <div className="mob-cat-heading">Categories</div>
              {allCategories.filter(c => !c.parent_id).map(cat => {
                const children = allCategories.filter(c => c.parent_id === cat.id);
                const isExpanded = mobileExpanded === `cat-${cat.id}`;
                return (
                  <div key={cat.id} className="mob-cat-item-wrap">
                    <div className="mob-cat-row">
                      <Link
                        to={`/products?category_id=${cat.id}`}
                        className="mob-cat-link"
                        onClick={() => setMobileOpen(false)}
                      >
                        {cat.name}
                      </Link>
                      {children.length > 0 && (
                        <button
                          className={`nav-chevron-btn${isExpanded ? ' open' : ''}`}
                          onClick={() => setMobileExpanded(isExpanded ? null : `cat-${cat.id}`)}
                        >
                          <FiChevronDown size={15} />
                        </button>
                      )}
                    </div>
                    {isExpanded && children.length > 0 && (
                      <div className="mob-cat-children">
                        {children.map(child => (
                          <Link
                            key={child.id}
                            to={`/products?category_id=${child.id}`}
                            className="mob-cat-child-link"
                            onClick={() => setMobileOpen(false)}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </nav>
      </header>
    </>
  );
}

export default Header;
